import { DEV } from 'cc/env';
import { Label, error, _decorator } from 'cc';
import I18n from '../../../I18n/I18n';
import GameDependency from '../../../System/GameDependency';
import { fillTextArgument } from '../../Utils/Utility';
import MultiThemeSource from '../MultiTheme/MultiThemeSource';

const { ccclass, property, requireComponent } = _decorator;

enum EventType {
    UNIT_CHANGED = 'UNIT_CHANGED',
}

const keyArray = ['Y', 'M', 'D', 'H', 'm', 's', 'ms'];
const updateFrequencyArray = [365 * 24 * 60 * 60, 30 * 24 * 60 * 60, 24 * 60 * 60, 60 * 60, 60, 1, 0.001];

function defaultCurrentTimeProvider() {
    return Date.now();
}

/**
 * 解析時區偏移量字串，並轉換為毫秒數
 * @param timezoneOffset 時區偏移量字串，格式為 "HH:MM"
 * @returns 偏移量對應的毫秒數
 */
function parseTimezoneOffset(timezoneOffset: string): number {
    try {
        const [hourOffset = '0', minuteOffset = '0'] = timezoneOffset?.split(':') ?? [];
        let hourOffsetNumber = parseInt(hourOffset, 10);
        if (Number.isNaN(hourOffsetNumber)) {
            hourOffsetNumber = 0;
        }
        let minuteOffsetNumber = parseInt(minuteOffset, 10);
        if (Number.isNaN(minuteOffsetNumber)) {
            minuteOffsetNumber = 0;
        }
        return (hourOffsetNumber * 60 + minuteOffsetNumber) * 60 * 1000;
    } catch (err) {
        error(`Failed to parse timezoneOffset: ${timezoneOffset}. Error: ${err}`);
        return 0;
    }
}

@ccclass('TimeLabel')
@requireComponent(Label)
export default class TimeLabel extends GameDependency {
    public static readonly EVENT_TYPE = EventType;
    @property({
        displayName: DEV && '倒數模式',
        tooltip: DEV && '是否為倒數時間格式',
    })
    public isCountTime: boolean = false;

    @property({
        displayName: DEV && '顯示當前時間',
        tooltip: DEV && '是否為單純顯示當前時間',
    })
    public displayCurrentTime: boolean = false;

    @property({ displayName: DEV && '多語系格式', tooltip: DEV && '是否為多語系格式' })
    public isLocalizedKeyformat: boolean = false;

    @property({
        displayName: DEV && '時間格式',
        multiline: true,
        tooltip:
            DEV &&
            '大括號中使用以下關鍵字設定時間，\nEx: {YY}/{MM}/{DD} {H}:{mm}:{s}\nYYYY: 西元年四位\nYY: 西元年後兩位\nMM: 月份補零兩位\nM: 月份不補零\nDD: 日期補零兩位\nD: 日期不補零\nHH: 小時補零兩位\nH: 小時不補零\nmm: 分鐘補零兩位\nm: 分鐘不補零\nss: 秒鐘補零兩位\ns: 秒鐘不補零',
        visible(this: TimeLabel) {
            return !this.isCountTime;
        },
    })
    public timeFormat: string = '{YYYY}/{MM}/{DD} {H}:{mm}:{s}';

    @property({
        displayName: DEV && '只顯示最大的時間單位',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    public onlyDisplayMaxUnit: boolean = false;

    @property({
        displayName: DEV && '倒數格式',
        tooltip:
            DEV &&
            '大括號中使用以下關鍵字設定時間，\nEx: {Y} {M} {D} {H} {m} {s}.{ms}\nYYYY: 西元年四位\nY: 年\nM: 月\nD: 日\nH: 小時\nm: 分鐘\ns: 秒\nms: 毫秒',
        visible(this: TimeLabel) {
            return this.isCountTime && !this.onlyDisplayMaxUnit;
        },
    })
    public countTimeFormat: string = '{Y} {M} {D} {H} {m} {s}.{ms}';

    @property({
        displayName: DEV && 'i18n的預設倒數文字',
        tooltip:
            DEV &&
            '啟用i18n卻沒有讀到時 使用的倒數文字\n大括號中使用以下關鍵字設定時間，\nEx: {Y} {M} {D} {H} {m} {s}.{ms}\nYYYY: 西元年四位\nY: 年\nM: 月\nD: 日\nH: 小時\nm: 分鐘\ns: 秒\nms: 毫秒',
        visible(this: TimeLabel) {
            return this.isCountTime && !this.onlyDisplayMaxUnit;
        },
    })
    public defaultI18nCountTimeText: string = '';

    @property({
        displayName: DEV && '不顯示為0的單位',
        tooltip: DEV && '剔除為0的時間單位, Ex: 0天',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    public readonly filterZeroUnit: boolean = false;

    @property({
        displayName: DEV && '最大時間單位轉換更新 MultiTheme',
        tooltip:
            DEV &&
            '時間單位轉換時更新 MultiTheme, Ex: D → H\n開啟時各時間單位都需要有有各自的 MultiThemeIndex\nIndex對照: 年:0, 月:1, 天:2, 小時:3, 分鐘:4, 秒:5, 毫秒:6',
        visible(this: TimeLabel) {
            return this.isCountTime && this.onlyDisplayMaxUnit;
        },
    })
    public readonly isUpdateMultiTheme: boolean = false;

    @property({
        type: MultiThemeSource,
        displayName: DEV && 'MultiTheme Source',
        tooltip: DEV && '欲更新的 MultiTheme Source',
        visible(this: TimeLabel) {
            return this.isCountTime && this.onlyDisplayMaxUnit && this.isUpdateMultiTheme;
        },
    })
    public readonly multiThemeSource: MultiThemeSource = null;

    @property({
        displayName: DEV && '年份單位',
        tooltip: DEV && 'Ex: 年 or Y \n也可依 isLocalizedKeyformat 套用多語系',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly yearUnit: string = 'year';

    @property({
        displayName: DEV && '年份是否補零',
        tooltip: DEV && '是否將年份補足為四位數',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly yearPadZeroStart: boolean = false;

    @property({
        displayName: DEV && '月份單位',
        tooltip: DEV && 'Ex: 月 or M \n也可依 isLocalizedKeyformat 套用多語系',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly monthUnit: string = 'month';

    @property({
        displayName: DEV && '月份是否補零',
        tooltip: DEV && '是否將月份補足為兩位數',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly monthPadZeroStart: boolean = false;

    @property({
        displayName: DEV && '天數單位',
        tooltip: DEV && 'Ex: 天 or D \n也可依 isLocalizedKeyformat 套用多語系',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly dayUnit: string = 'day';

    @property({
        displayName: DEV && '天數是否補零',
        tooltip: DEV && '是否將天數補足為兩位數',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly dayPadZeroStart: boolean = false;

    @property({
        displayName: DEV && '小時單位',
        tooltip: DEV && 'Ex: 小時 or H \n也可依 isLocalizedKeyformat 套用多語系',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly hourUnit: string = 'hour';

    @property({
        displayName: DEV && '小時是否補零',
        tooltip: DEV && '是否將小時補足為兩位數',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly hourPadZeroStart: boolean = false;

    @property({
        displayName: DEV && '分鐘單位',
        tooltip: DEV && 'Ex: 分 or min \n也可依 isLocalizedKeyformat 套用多語系',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly minuteUnit: string = 'min';

    @property({
        displayName: DEV && '分鐘是否補零',
        tooltip: DEV && '是否將分鐘補足為兩位數',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly minutePadZeroStart: boolean = false;

    @property({
        displayName: DEV && '秒鐘單位',
        tooltip: DEV && 'Ex: 秒 or s \n也可依 isLocalizedKeyformat 套用多語系',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly secondsUnit: string = 's';

    @property({
        displayName: DEV && '秒鐘是否補零',
        tooltip: DEV && '是否將秒鐘補足為兩位數',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly secondsPadZeroStart: boolean = false;

    @property({
        displayName: DEV && '毫秒單位',
        tooltip: DEV && 'Ex: 毫秒 or ms \n也可依 isLocalizedKeyformat 套用多語系',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly millisecondsUnit: string = 'ms';

    @property({
        displayName: DEV && '更新頻率倍數',
        tooltip: DEV && '依據倒數格式的最小單位，設定更新頻率倍數\nEx: 最小單位為秒時，更新頻率為 1 / x 秒',
        visible(this: TimeLabel) {
            return this.isCountTime;
        },
    })
    private readonly updateFrequencyScale: number = 1;

    @property
    private _displayTimeZone: boolean = false;

    @property
    private _timeZoneFormat: string = ' ({0})';

    @property
    private _timeAndTimeZoneFormat: string = '{0}{1}';

    private _time: number = -1;
    private _label: Label = null;
    private _timeoutCallback: () => void = null;
    private _updateFrequency: number = 0;
    private _updateTick: number = 0;
    private _lastUnit: string = '';
    private _unitArray: string[] = [];
    private _padZeroStartArray: boolean[] = [];
    private _currentTimeProvider: () => number = defaultCurrentTimeProvider;
    private readonly padZeroStartLengthArray: number[] = [4, 2, 2, 2, 2, 2, 0];

    @property({
        displayName: DEV && '顯示時區',
        tooltip: DEV && '是否顯示時區',
    })
    public get displayTimeZone(): boolean {
        return this._displayTimeZone;
    }
    public set displayTimeZone(value: boolean) {
        if (this._displayTimeZone === value) {
            return;
        }
        this._displayTimeZone = value;
        this._formatDateTime();
    }

    @property({
        displayName: DEV && '時區格式',
        tooltip: DEV && '設定時區格式',
        multiline: true,
        visible(this: TimeLabel) {
            return this.displayTimeZone;
        },
    })
    public get timeZoneFormat(): string {
        return this._timeZoneFormat;
    }
    public set timeZoneFormat(value: string) {
        if (this._timeZoneFormat === value) {
            return;
        }
        this._timeZoneFormat = value;
        this._formatDateTime();
    }

    @property({
        displayName: DEV && '時間加時區格式',
        tooltip: DEV && '設定時間加時區格式，{0}為時間，{1}為時區\nEx: {0} {1} → 2024/01/01 UTC+08:00',
        multiline: true,
        visible(this: TimeLabel) {
            return this.displayTimeZone;
        },
    })
    public get timeAndTimeZoneFormat(): string {
        return this._timeAndTimeZoneFormat;
    }
    public set timeAndTimeZoneFormat(value: string) {
        if (this._timeAndTimeZoneFormat === value) {
            return;
        }
        this._timeAndTimeZoneFormat = value;
        this._formatDateTime();
    }

    public get time(): number {
        return this._time;
    }
    public set time(value: number) {
        this._time = value;
        if (this.isCountTime) {
            this._resetTimer();
            this._updateCountTime();
        } else if (this.displayCurrentTime) {
            let minUnit = 0;
            keyArray.forEach((key, i) => {
                if (this.displayCountTime.includes(`{${key}}`)) {
                    minUnit = i;
                }
            });
            this._updateFrequency = updateFrequencyArray[minUnit];
            this._formatDateTime();
        } else {
            this._formatDateTime();
        }
    }

    public set currentTimeProvider(value: () => number) {
        if (this._currentTimeProvider === value) {
            return;
        }
        this._currentTimeProvider = value;
        if (this.isCountTime) {
            this._resetTimer();
            this._updateCountTime();
        }
    }

    private get label() {
        if (!this._label) {
            this._label = this.getComponent(Label);
        }
        return this._label;
    }

    private get displayCountTime() {
        if (this.useLocalizedKeyformat) {
            return this.game.i18n.getMessage(this.countTimeFormat) || this.defaultI18nCountTimeText || this.countTimeFormat;
        }
        if (this.isLocalizedKeyformat) {
            return this.defaultI18nCountTimeText || this.countTimeFormat;
        }
        return this.countTimeFormat;
    }

    private get useLocalizedKeyformat() {
        if (!this.isLocalizedKeyformat) {
            return false;
        }
        // check if i18n is available
        if (!this.game.i18n || !this.game.i18n.currentLanguage) {
            return false;
        }
        return true;
    }

    private get unitArray() {
        if (this._unitArray.length === 0) {
            this._unitArray = [this.yearUnit, this.monthUnit, this.dayUnit, this.hourUnit, this.minuteUnit, this.secondsUnit, this.millisecondsUnit];
        }
        return this._unitArray;
    }

    private get padZeroStartArray() {
        if (this._padZeroStartArray.length === 0) {
            this._padZeroStartArray = [
                this.yearPadZeroStart,
                this.monthPadZeroStart,
                this.dayPadZeroStart,
                this.hourPadZeroStart,
                this.minutePadZeroStart,
                this.secondsPadZeroStart,
                false,
            ];
        }
        return this._padZeroStartArray;
    }

    public static formatDateTime(time: number, format: string, timeZoneFormat?: string, timeAndTimeZoneFormat?: string, timezoneOffset?: string): string {
        const offset = parseTimezoneOffset(timezoneOffset);
        const date = new Date(time + offset);
        const useUTCDate = !!timezoneOffset;
        const year = useUTCDate ? date.getUTCFullYear() : date.getFullYear();
        const month = useUTCDate ? date.getUTCMonth() + 1 : date.getMonth() + 1;
        const day = useUTCDate ? date.getUTCDate() : date.getDate();
        const hour = useUTCDate ? date.getUTCHours() : date.getHours();
        const minute = useUTCDate ? date.getUTCMinutes() : date.getMinutes();
        const second = useUTCDate ? date.getUTCSeconds() : date.getSeconds();
        const templateKeyValueMap = [
            { key: '{YYYY}', value: year },
            { key: '{YY}', value: year % 100 },
            { key: '{MM}', value: month.toString().padStart(2, '0') },
            { key: '{M}', value: month },
            { key: '{DD}', value: day.toString().padStart(2, '0') },
            { key: '{D}', value: day },
            { key: '{HH}', value: hour.toString().padStart(2, '0') },
            { key: '{H}', value: hour },
            { key: '{mm}', value: minute.toString().padStart(2, '0') },
            { key: '{m}', value: minute },
            { key: '{ss}', value: second.toString().padStart(2, '0') },
            { key: '{s}', value: second },
        ];
        let str = format;
        templateKeyValueMap.forEach(({ key, value }) => {
            str = str.replace(key, `${value}`);
        });
        if (timeZoneFormat) {
            const timeZoneString = fillTextArgument(timeZoneFormat, [TimeLabel.getTimezoneString(timezoneOffset)]);
            if (timeAndTimeZoneFormat) {
                str = fillTextArgument(timeAndTimeZoneFormat, [str, timeZoneString]);
            } else {
                str += timeZoneString;
            }
        }

        return str;
    }

    public static getTimezoneString(timezoneOffset?: string): string {
        if (timezoneOffset) {
            return `UTC${timezoneOffset}`;
        }
        const offsetMinutes = new Date().getTimezoneOffset();
        // getTimezoneOffset() = UTC - LocalTime，因此符號需反轉
        const sign = offsetMinutes <= 0 ? '+' : '-'; //
        const absMinutes = Math.abs(offsetMinutes);
        const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
        const minutes = String(absMinutes % 60).padStart(2, '0');

        return `UTC${sign}${hours}:${minutes}`;
    }

    protected onLoad(): void {
        if (this.displayCurrentTime) {
            this.time = this._currentTimeProvider();
        }
        if (this.isLocalizedKeyformat) {
            if (this.isCountTime) {
                this.game.i18n.on(I18n.EVENT_TYPE.LanguageChanged, this._updateCountTime, this);
            } else {
                this.game.i18n.on(I18n.EVENT_TYPE.LanguageChanged, this._formatDateTime, this);
            }
        }
    }

    protected onDestroy(): void {
        if (this.isLocalizedKeyformat) {
            if (this.isCountTime) {
                this.game.i18n.off(I18n.EVENT_TYPE.LanguageChanged, this._updateCountTime, this);
            } else {
                this.game.i18n.off(I18n.EVENT_TYPE.LanguageChanged, this._formatDateTime, this);
            }
        }
    }

    protected update(dt: number): void {
        if (this.displayCurrentTime) {
            this._updateTick += dt;
            if (this._updateTick >= this._updateFrequency) {
                this._time = this._currentTimeProvider();
                this._formatDateTime();
            }
        }
    }

    private _resetTimer() {
        if (!this._timeoutCallback) {
            this._timeoutCallback = () => {
                if (!this.node.activeInHierarchy) {
                    return;
                }
                this._updateCountTime();
            };
        }
        this.unschedule(this._timeoutCallback);
        if (this.onlyDisplayMaxUnit) {
            this.schedule(this._timeoutCallback);
        } else {
            let minUnit = 0;
            keyArray.forEach((key, i) => {
                if (this.displayCountTime.includes(`{${key}}`)) {
                    minUnit = i;
                }
            });
            // use the next smaller unit as update frequency to ensure smooth countdown
            this._updateFrequency = updateFrequencyArray[Math.min(minUnit + 1, updateFrequencyArray.length - 1)];
            this.schedule(this._timeoutCallback, this._updateFrequency / this.updateFrequencyScale);
        }
    }

    private _updateCountTime() {
        let diffTime = Math.max(0, this._time - this._currentTimeProvider());
        if (this.onlyDisplayMaxUnit) {
            let str = '';
            for (let i = 0; i < keyArray.length; i++) {
                const unitTime = updateFrequencyArray[i] * 1000;
                const value = Math.floor(diffTime / unitTime);
                const unit = this.unitArray[i];
                if (unit === '') {
                    continue;
                }
                const padZeroStart = this.padZeroStartArray[i];
                const padZeroStartLength = this.padZeroStartLengthArray[i];
                const valueString = padZeroStart ? value.toString().padStart(padZeroStartLength, '0') : `${value}`;
                str = `${valueString}${this.useLocalizedKeyformat ? this.game.i18n.getMessage(unit) : unit}`;
                if (this.filterZeroUnit && value <= 0) {
                    continue;
                }
                if (this._lastUnit !== unit) {
                    this.emit(TimeLabel.EVENT_TYPE.UNIT_CHANGED);
                    if (this.isUpdateMultiTheme && this.multiThemeSource) {
                        this.multiThemeSource.theme = i;
                    }
                }
                this._lastUnit = unit;
                break;
            }
            this.label.string = str;
        } else {
            let str = this.displayCountTime;
            keyArray.forEach((key, i) => {
                if (!str.includes(`{${key}}`)) {
                    return;
                }
                const unitTime = updateFrequencyArray[i] * 1000;
                const value = Math.floor(diffTime / unitTime);
                if (this.filterZeroUnit && value <= 0) {
                    str = str.replace(`{${key}}`, ``);
                    return;
                }
                const unit = this.unitArray[i];
                const padZeroStart = this.padZeroStartArray[i];
                const padZeroStartLength = this.padZeroStartLengthArray[i];
                const valueString = padZeroStart ? value.toString().padStart(padZeroStartLength, '0') : `${value}`;
                if (unit !== '') {
                    str = str.replace(`{${key}}`, `${valueString}${this.useLocalizedKeyformat ? this.game.i18n.getMessage(unit) : unit}`);
                } else {
                    str = str.replace(`{${key}}`, `${valueString}`);
                }
                diffTime -= value * unitTime;
            });
            this.label.string = str;
        }
    }

    private _formatDateTime() {
        const template = this.useLocalizedKeyformat ? this.game.i18n.getMessage(this.timeFormat) : this.timeFormat;
        const timeZoneFormat = this.displayTimeZone ? this.timeZoneFormat : '';
        const timeAndTimeZoneFormat = this.displayTimeZone ? this.timeAndTimeZoneFormat : '';
        this.label.string = TimeLabel.formatDateTime(this._time, template, timeZoneFormat, timeAndTimeZoneFormat, this.game.config.timezoneOffset);
    }
}
